import { useEffect, Suspense, lazy } from "react";
import { useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useRoutes,
} from "react-router-dom";
import { doesPathExist } from "./helpers/AccessControlHandlers";
import useGeneralApi from "./Hooks/useGeneralApi";

// Layouts - Keep these as regular imports since they're used frequently
import AuthLayout from "./layouts/AuthLayout";
import OnBoardingLayout from "./layouts/OnBoardingLayout";
import UserLayout from "./layouts/UserLayout";
import DefaultLayout from "./layouts/DefaultLayout";
import Loader from "./components/Loader";
import WalletGuard from "./guards/walletGuard";
import WalletAgreement from "./components/WalletAgreement/index"
// Lazy load all page components
const Login = lazy(() => import("./pages/auth/Login"));
const SignUp1 = lazy(() => import("./pages/auth/SignUp1"));
const OnBoarding = lazy(() => import("./pages/OnBoarding"));
const ResidentDefaultDashboard = lazy(() => import("./pages/auth/ResidentDefaultDashboard"));

// User Dashboard Components
const DashboardV1 = lazy(() => import("./pages/user/DashboardV1"));
const DefaultDashboard = lazy(() => import("./pages/user/Dashboard"));
const AmenitiesDashboard = lazy(() => import("./pages/user/Dashboard/AmenitiesDashboard"));
const AmenitiesDetailsPage = lazy(() => import("./pages/user/Dashboard/AmenitiesDashboard/AmenitiesDetailsPage"));
const AmenitiesBookingPage = lazy(() => import("./pages/user/Dashboard/AmenitiesDashboard/AmenitiesBookingPage"));

// Management Components
const Management = lazy(() => import("./pages/user/Management"));
const Amenities = lazy(() => import("./pages/user/Management/Amenities"));
const UserRole = lazy(() => import("./pages/user/Management/user-role"));

// Account Components
const LedgerAccounts = lazy(() => import("./pages/user/Accounts/LedgerAccounts"));
const SetupAccount = lazy(() => import("./pages/user/Accounts/SetupAccount"));
const SetupExpense = lazy(() => import("./pages/user/Accounts/SetupExpense"));
const ExpenseDetailPage = lazy(() => import("./pages/user/Accounts/SetupExpense/ExpenseDetailPage"));

// Bills Components
const BillsCollectionPage = lazy(() => import("./pages/user/Bills/BillsCollectionPage"));
const GenerateBillPage = lazy(() => import("./pages/user/Bills/GenerateBillPage"));

// Settings Components
const AdditionalSettings = lazy(() => import("./pages/user/Settings/AdditionalSettings"));
const Announcements = lazy(() => import("./pages/user/Settings/Announcements"));
const Approvals = lazy(() => import("./pages/user/Settings/Approvals"));
const ChangePassword = lazy(() => import("./pages/user/Settings/ChangePassword"));
const CommunitySettings = lazy(() => import("./pages/user/Settings/CommunitySettings"));
const Complains = lazy(() => import("./pages/user/Settings/Complains"));
const Notifications = lazy(() => import("./pages/user/Settings/Notifications"));
const PrivacyPolicy = lazy(() => import("./pages/user/Settings/PrivacyPolicy"));
const TermsNConditions = lazy(() => import("./pages/user/Settings/TermsNConditions"));
const DeleteAccount = lazy(() => import("./pages/user/Settings/DeleteAccount"));

// Approval Components
const JoinApprovalsPage = lazy(() => import("./pages/user/Settings/Approvals/JoinApprovalsPage"));
const BankApprovalPage = lazy(() => import("./pages/user/Settings/Approvals/BankApprovalPage"));
const VisitorApprovalPage = lazy(() => import("./pages/user/Settings/Approvals/VisitorApprovalsPage"));

// Other Components
const CommunityPolicy = lazy(() => import("./pages/user/CommunityPolicy"));
const NotFound = lazy(() => import("./pages/user/NotFound"));
const PaymentHistory = lazy(() => import("./pages/user/PaymentHistory"));
const Receptionist = lazy(() => import("./pages/user/Receptionist"));
const ResidentPayments = lazy(() => import("./pages/user/ResidentPayments"));
const ResidentTransactionsDetailsPage = lazy(() => import("./pages/user/ResidentPayments/ResidentTransactionsDetailsPage"));
const ResidentVisitors = lazy(() => import("./pages/user/ResidentVisitors"));
const VisitorsDetailsPage = lazy(() => import("./pages/user/ResidentVisitors/VisitorsDetailsPage"));
const Subscription = lazy(() => import("./pages/user/Subscription"));
const UsersPage = lazy(() => import("./pages/user/UsersPage"));
const UsersDetailsPage = lazy(() => import("./pages/user/UsersPage/UsersDetailsPage"));
const CreateOrJoinCommunity = lazy(() => import("./pages/user/CreateOrJoinCommunity"));
const SubscriptionTimer = lazy(() => import("./pages/user/SubscriptionTimer"));
const ResidentAnnouncementsListings = lazy(() => import("./pages/user/AnnouncementsResidentListing"));
const Help = lazy(() => import("./pages/user/Helps"));
const Reports = lazy(() => import("./pages/user/Reports"));
//Wallets
const Beneficiary = lazy(() => import("./pages/user/Beneficiary"))
const TransactionHistory = lazy(() => import("./pages/user/TransactionHistory"))
const Wallet = lazy(() => import("./pages/user/Wallet"));
const Widthdraw = lazy(() => import("./pages/user/Withdraw"))
const ScheduleOfChargesPage = lazy(() => import("./pages/user/ScheduleOfCharges"));
// Loading component for Suspense fallback
const LoadingFallback = () => (

  <Loader />

);

export default function Router() {
  const {
    token,
    isSetupCompleted,
    isSuperUser,
    isLogin,
    menu,
    communitiesList,
    pendingCommunityList,
    isSkip,
    userType,
    user,
    roleId,
    communityName,
    lastActiveCommunity,
    roleName,
  } = useSelector((state) => state?.authReducer);

  // useGeneralApi();
  const navigate = useNavigate();
  const location = useLocation();
  const val = location.pathname.split("/")[1];

  // Define paths that should always be accessible regardless of menu permissions
  const explicitlyAllowedPaths = [
    "create-community",
    "subscription-timer",
    "resident-announcements",
    "helps"
    // Add more paths here if needed
  ];

  useEffect(() => {
    if (location.hash.startsWith("#/")) {
      navigate(location.hash.replace("#", ""), { replace: true });
    }
  }, [location, navigate]);

  const authRoutes = useRoutes([
    {
      path: "/",
      element: <AuthLayout />,
      children: [
        {
          path: "/",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Login />
            </Suspense>
          ),
          index: true
        },
        {
          path: "signup",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SignUp1 />
            </Suspense>
          )
        },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/" />,
    },
  ]);

  const communityRoutes = useRoutes([
    {
      path: "/",
      element: <OnBoardingLayout />,
      children: [
        {
          path: "/",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <OnBoarding />
            </Suspense>
          ),
          index: true
        },
        {
          path: "*",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          )
        },
      ],
    },
  ]);

  const userRoutes = useRoutes([
    {
      path: "/",
      element: <UserLayout />,
      children: [
        {
          path: "/",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              {/* <WalletAgreement /> */}
              {/* <h2>Dashboard</h2> */}
              <DashboardV1 />
            </Suspense>
          ),
          index: true
        },
        {
          path: "/amenityDashboard",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AmenitiesDashboard />
            </Suspense>
          )
        },
        {
          path: "/amenityDetails/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AmenitiesDetailsPage />
            </Suspense>
          )
        },
        {
          path: "/amenityBooking/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AmenitiesBookingPage />
            </Suspense>
          )
        },
        {
          path: "/complaints",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Complains />
            </Suspense>
          )
        },
        {
          path: "/management",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Management />
            </Suspense>
          )
        },
        {
          path: "/generateBill",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <GenerateBillPage />
            </Suspense>
          )
        },
        {
          path: "/paymentCollection",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BillsCollectionPage />
            </Suspense>
          )
        },
        {
          path: "/accounts",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SetupAccount />
            </Suspense>
          )
        },
        {
          path: "/accountsLedger",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <LedgerAccounts />
            </Suspense>
          )
        },
        {
          path: "/expense",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <SetupExpense />
            </Suspense>
          )
        },
        {
          path: "/expense/:code",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ExpenseDetailPage />
            </Suspense>
          )
        },
        {
          path: "/ammenities",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Amenities />
            </Suspense>
          )
        },
        {
          path: "/users",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <UsersPage />
            </Suspense>
          )
        },
        {
          path: "/users/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <UsersDetailsPage />
            </Suspense>
          )
        },
        {
          path: "/userRole",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <UserRole />
            </Suspense>
          )
        },
        {
          path: "/notifications",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Notifications />
            </Suspense>
          )
        },
        {
          path: "/bank-approval",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <BankApprovalPage type={4} name={"Bank Approvals"} />
            </Suspense>
          )
        },
        {
          path: "/visitor-approval",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <VisitorApprovalPage type={3} name={"Visitor Approvals"} />
            </Suspense>
          )
        },
        {
          path: "/join-approval",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <JoinApprovalsPage type={1} name={"Join  Approvals"} />
            </Suspense>
          )
        },
        {
          path: "/announcements",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Announcements />
            </Suspense>
          )
        },
        {
          path: "/changePassword",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ChangePassword />
            </Suspense>
          )
        },
        {
          path: "/termsNConditions",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TermsNConditions />
            </Suspense>
          )
        },
        {
          path: "/privacyPolicy",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PrivacyPolicy />
            </Suspense>
          )
        },
        {
          path: "/payments",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentPayments />
            </Suspense>
          )
        },
        {
          path: "/payments/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentTransactionsDetailsPage />
            </Suspense>
          )
        },
        {
          path: "/visitors",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentVisitors />
            </Suspense>
          )
        },
        {
          path: "/visitors/:id",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <VisitorsDetailsPage />
            </Suspense>
          )
        },
        {
          path: "/receptionist",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Receptionist />
            </Suspense>
          )
        },
        {
          path: "/communitySettings",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CommunitySettings />
            </Suspense>
          )
        },
        {
          path: "/subscription",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Subscription />
            </Suspense>
          )
        },
        {
          path: "/communityPolicy",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <CommunityPolicy />
            </Suspense>
          )
        },
        {
          path: "/additionalSettings",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <AdditionalSettings />
            </Suspense>
          )
        },
        {
          path: "/paymentHistory",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <PaymentHistory />
            </Suspense>
          )
        },
        {
          path: "/resident-announcements",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentAnnouncementsListings />
            </Suspense>
          )
        },
        {
          path: "/reports",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Reports />
            </Suspense>
          )
        },
        {
          path: "/wallet",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Wallet />
              {/* <Beneficiary /> */}
              {/* <Widthdraw /> */}
              {/* <TransactionHistory /> */}

            </Suspense>
          )
        },
        {
          path: "/transaction-history",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <TransactionHistory />
            </Suspense>
          )
        },
        {
          path: "/withdraw",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WalletGuard>
                <Widthdraw />
              </WalletGuard>
            </Suspense>
          )
        },
        {
          path: "/beneficiaries",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WalletGuard>
                <Beneficiary />
              </WalletGuard>
            </Suspense>
          )
        },
        {
          path: "/schedule-of-charges",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <WalletGuard>
                <ScheduleOfChargesPage />
              </WalletGuard>
            </Suspense>
          )
        }
      ],
    },
  ]);

  // const defaultRoutes = useRoutes([
  //   {
  //     path: "/",
  //     element: <UserLayout />,
  //     children: [
  //       {
  //         path: "/",
  //         element: (
  //           <Suspense fallback={<LoadingFallback />}>
  //             <ResidentDefaultDashboard />
  //           </Suspense>
  //         ),
  //         index: true
  //       },
  //       {
  //         path: "*",
  //         element: (
  //           <Suspense fallback={<LoadingFallback />}>
  //             <NotFound />
  //           </Suspense>
  //         )
  //       },
  //     ],
  //   },


  // ]);


  const defaultRoutes = useRoutes([
    {
      path: "/",
      element: <DefaultLayout />,
      children: [
        // Root route - OnBoarding as the index page
        {
          index: true,
          element: <OnBoarding />
        },

        // Default dashboard route with nested children
        {
          path: "default-dashboard",  // Relative path, no leading slash
          element: <UserLayout />,
          children: [
            {
              index: true,  // This makes it the default child route
              element: (
                <Suspense fallback={<LoadingFallback />}>
                  <ResidentDefaultDashboard />
                </Suspense>
              ),
            },
            // You can add more child routes here if needed
            // {
            //   path: "profile",  // Would be accessible at /default-dashboard/profile
            //   element: <Profile />
            // }
          ],
        },

        // Catch-all route for 404
        {
          path: "*",
          element: <NotFound />
        },
      ],
    },
  ]);
  const notFoundRoutes = useRoutes([
    {
      path: "/",
      element: <UserLayout />,
      children: [
        {
          path: "*",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          ),
        },
      ],
    },
  ]);

  // Routes for paths that don't need UserLayout wrapper
  const standaloneRoutes = useRoutes([
    {
      path: "/create-community",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <CreateOrJoinCommunity />
        </Suspense>
      ),
    },
    {
      path: "/subscription-timer",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <SubscriptionTimer />
        </Suspense>
      ),
    },
    {
      path: "/",
      element: <UserLayout />,
      children: [
        {
          index: true,
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentAnnouncementsListings />
            </Suspense>
          )
        },
        {
          path: "resident-announcements",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <ResidentAnnouncementsListings />
            </Suspense>
          )
        },
        {
          path: "/helps",
          element: (
            <Suspense fallback={<LoadingFallback />}>
              <Help />
            </Suspense>
          )
        },
      ],
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <NotFound />
        </Suspense>
      ),
    },
  ]);

  // Helper function to check if path should be accessible
  const isPathAccessible = (pathSegment) => {
    return explicitlyAllowedPaths.includes(pathSegment) || doesPathExist(menu, pathSegment);
  };

  if (isLogin) {
    if (!isSkip) {

      console.log("Is Skip ")
      return communityRoutes;
    }
    else {
      if (roleName === "QP_CB_User") { // jis ny setup clear nhi kia hy default community 
        console.log("QP_CB_USER")
        return defaultRoutes;
      }
      else {// jis ny setup complete kia hy
        // Check if it's an explicitly allowed path that doesn't need UserLayout
        if (explicitlyAllowedPaths.includes(val)) {
          console.log("Stand Alone Routes")
          return standaloneRoutes;
        }
        // Check if path exists in menu for regular UserLayout routes
        else if (doesPathExist(menu, val)) {
          console.log("User Routes")
          return userRoutes;
        } else {
          return notFoundRoutes;
        }
      }
    }
  }
  else {
    return authRoutes;
  }
}